// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Utils
import { isTerminatedExamReport } from "src/utils/exams/examReportsHelper";

// Constants
import { examCardEnums } from "src/constants/enums";

const ProgressiveLensGraph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.PROGRESSIVE_SIMULATION.sectionName,
      }),
    [exam, examReport]
  );

  const progressiveList = useMemo(
    () =>
      examReport?.state?.lenses?.filter(
        (lens) => lens.productType === "Progressive"
      ) ?? [],
    [examReport?.state?.lenses]
  );

  // Progressive lenses can only be preferred for both eyes
  // You can't favorite lenses for individual eyes
  const preferredBothProgressive = useMemo(
    () =>
      examReport?.state?.lenses?.filter(
        (lens) =>
          lens.productType === "Progressive" &&
          lens.preferred.leftEye &&
          lens.preferred.rightEye
      ) ?? [],
    [examReport?.state?.lenses]
  );

  return (
    <Grid container gap="20px">
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            {t("Among the following progressive lens options")}:
          </Typography>
          <Box
            component="ul"
            sx={{
              columns: "auto",
              columnWidth: "200px",
              listStyle: "disc",
              paddingLeft: "20px",
              textAlign: "left",
              fontSize: "14px",
            }}
          >
            {progressiveList?.map((lens, index) => (
              <li key={`${lens.lensType}-${index}`}>{t(lens.lensType)}</li>
            ))}
          </Box>
          {preferredBothProgressive.length > 0 && (
            <>
              <Typography sx={{ fontSize: "14px" }}>
                {t("For both eyes, the patient preferred")}:
              </Typography>
              <Box
                component="ul"
                sx={{
                  columns: "auto",
                  columnWidth: "200px",
                  listStyle: "disc",
                  paddingLeft: "20px",
                  textAlign: "left",
                  fontSize: "14px",
                }}
              >
                {preferredBothProgressive?.map((lens, index) => (
                  <li key={`${lens.lensType}-${index}`}>{t(lens.lensType)}</li>
                ))}
              </Box>
            </>
          )}
          {!preferredBothProgressive.length && (
            <Typography sx={{ fontSize: "14px" }}>
              {t("The patient did not select any preference.")}
            </Typography>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ProgressiveLensGraph;
